// Thin compound wrapper around Radix Tabs. Delegates the WAI-ARIA tab
// pattern (role + aria-* wiring, roving tabindex, arrow-key navigation,
// Home / End, focus-follows-selection) to `@radix-ui/react-tabs`; we
// add the design-system surface (BEM classes, the per-Tab `count` slot,
// the aria-label on the tablist) on top.
//
// Component shape preserved from the hand-rolled version:
//   <Tabs value={x} on_value_change={...}>
//     <Tabs.List aria-label="Filter">
//       <Tabs.Tab value="all" count={7}>All</Tabs.Tab>
//       ...
//     </Tabs.List>
//     <Tabs.Panel value="all">...</Tabs.Panel>
//   </Tabs>

import * as RadixTabs from '@radix-ui/react-tabs';
import { type ComponentProps, type ReactNode, useId } from 'react';

import { classlist } from '~/_shared/design-system/lib/classlist';

import './tabs.css';

interface TabsRootProps
	extends Omit<
		ComponentProps<typeof RadixTabs.Root>,
		'value' | 'onValueChange' | 'defaultValue'
	> {
	value: string;
	on_value_change: (value: string) => void;
	children: ReactNode;
}

const TabsRoot = ({
	value,
	on_value_change,
	children,
	id,
	className,
	...rest
}: TabsRootProps) => {
	// Instance-unique default id (explicit `id` prop wins) — see BaseButton.
	const auto_id = useId();
	return (
		<RadixTabs.Root
			{...rest}
			id={id ?? `Tabs-${auto_id}`}
			value={value}
			onValueChange={on_value_change}
			className={classlist('tabs', className)}
		>
			{children}
		</RadixTabs.Root>
	);
};

interface TabsListProps {
	children?: ReactNode;
	'aria-label'?: string;
}

const TabsList = ({
	children,
	'aria-label': aria_label = 'Tabs',
}: TabsListProps) => (
	<RadixTabs.List aria-label={aria_label} className='tabs__list'>
		{children}
	</RadixTabs.List>
);

interface TabsTabProps {
	value: string;
	children: ReactNode;
	count?: number;
	disabled?: boolean;
}

const TabsTab = ({
	value,
	children,
	count,
	disabled = false,
}: TabsTabProps) => (
	<RadixTabs.Trigger
		value={value}
		disabled={disabled}
		className={classlist('tabs__tab', disabled && 'tabs__tab--disabled')}
	>
		<span className='tabs__tab-label'>{children}</span>
		{count !== undefined ? (
			<span className='tabs__tab-count'>{count}</span>
		) : null}
	</RadixTabs.Trigger>
);

interface TabsPanelProps {
	value: string;
	children: ReactNode;
}

const TabsPanel = ({ value, children }: TabsPanelProps) => (
	<RadixTabs.Content value={value} className='tabs__panel' tabIndex={-1}>
		{children}
	</RadixTabs.Content>
);

const Tabs = Object.assign(TabsRoot, {
	List: TabsList,
	Tab: TabsTab,
	Panel: TabsPanel,
});

export type { TabsListProps, TabsPanelProps, TabsRootProps, TabsTabProps };
export { Tabs };
